import { useState, useEffect } from "react";
import { Grid, Card, Icon, Autocomplete, CircularProgress, Divider, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import { postUpload, get, postNoResponse, baseURL } from "utils/WebService";
import { useMaterialUIController } from "context";

const SYSTEM_FIELDS = [
    { value: "", label: "-- Ignorar Coluna --" },
    { value: "tbProcesso.ccProcesso", label: "Processo: Nº do Processo (Obrigatório)" },
    { value: "tbProcesso.cvTipoCaso", label: "Processo: Tipo Caso" },
    { value: "tbProcesso.ccParteContraria", label: "Processo: Parte Contrária" },
    { value: "tbProcesso.cvStatus", label: "Processo: Status" },
    { value: "tbProcesso.cvProcessoEletronico", label: "Processo: Processo Eletrônico" },
    { value: "tbProcesso.cvTipoSistema", label: "Processo: Tipo Sistema" },
    { value: "tbProcesso.ccMensagem", label: "Processo: Mensagem" },
    { value: "tbProcesso.ccMotivoSuspensao", label: "Processo: Motivo Suspensão" },
    { value: "tbCaso.ccNomeCaso", label: "Caso: Nome" },
    { value: "tbCaso.ccDocumento", label: "Caso: Documento" },
    { value: "tbCaso.cvStatus", label: "Caso: Status" },
    { value: "tbProcessoCadastroBasico.ccNumWibs", label: "Cadastro Básico: ID WIBS" },
    { value: "tbProcessoCadastroBasico.ccNumCNJ", label: "Cadastro Básico: Nº CNJ" },
    { value: "tbProcessoCadastroBasico.ccNumPastaCliente", label: "Cadastro Básico: Nº Pasta Cliente" },
    { value: "tbProcessoCadastroBasico.cdDataDistribuicao", label: "Cadastro Básico: Data Distribuição" },
    { value: "tbProcessoCadastroBasico.cvValorCausaNominal", label: "Cadastro Básico: Valor Causa" },
    { value: "tbPessoa.ccNomeCompleto", label: "Pessoa: Nome Completo (Cliente/Autor/Réu)" },
    { value: "tbPessoa.ccCpfCnpj", label: "Pessoa: CPF/CNPJ" },
    { value: "ativo", label: "Sistema: Flag ATIVO? (SIM/NÃO)" }
];

function MigracaoProcessos() {
    const [file, setFile] = useState(null);
    const [step, setStep] = useState(1);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    
    // Step 1 states
    const [tipoImportacao, setTipoImportacao] = useState("PESSOAS");
    const [loadingHeaders, setLoadingHeaders] = useState(false);
    
    // Step 2 states
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [savingMapping, setSavingMapping] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    // Feedback states
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Load saved mapping on mount
    useEffect(() => {
        loadSavedMapping();
    }, []);

    const loadSavedMapping = async () => {
        try {
            const res = await get("/process-migration/mapping");
            if (res) {
                setMapping(res);
            }
        } catch (e) {
            console.error("Erro ao carregar mapeamento salvo", e);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selected = e.target.files[0];
            if (selected.type === "text/xml" || selected.name.endsWith(".xml")) {
                setFile(selected);
            } else {
                setErrorMessage("Formato inválido. Por favor, selecione um arquivo XML Spreadsheet.");
                setErrorSB(true);
                setFile(null);
            }
        }
    };

    const handleExtractHeaders = async () => {
        if (!file) return;
        setLoadingHeaders(true);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await postUpload("/process-migration/extract-headers", formData);
            const cols = res?.data || res || [];
            if (cols.length === 0) {
                throw new Error("Nenhuma coluna encontrada no arquivo XML.");
            }
            setHeaders(cols);
            
            // Initialize mapping state for headers that don't have saved mapping
            setMapping(prev => {
                const newMap = { ...prev };
                cols.forEach(col => {
                    if (newMap[col] === undefined) newMap[col] = "";
                });
                return newMap;
            });
            setStep(2);
        } catch (error) {
            setErrorMessage(error.response?.data?.message || error.message || "Erro ao ler as colunas do arquivo.");
            setErrorSB(true);
        } finally {
            setLoadingHeaders(false);
        }
    };

    const handleSaveMapping = async () => {
        setSavingMapping(true);
        try {
            await postNoResponse("/process-migration/mapping", mapping);
            setSuccessSB(true);
            setErrorMessage("Mapeamento salvo com sucesso!");
        } catch (error) {
            setErrorMessage("Erro ao salvar mapeamento.");
            setErrorSB(true);
        } finally {
            setSavingMapping(false);
        }
    };

    const handleStartMigration = async () => {
        // Validate required fields based on tipoImportacao
        const mappedValues = Object.values(mapping);
        if (tipoImportacao === "PESSOAS" || tipoImportacao === "FASE0") {
            if (!mappedValues.includes("tbPessoa.ccNomeCompleto")) {
                setErrorMessage("Para esta fase, mapeie a coluna que contém o Nome (tbPessoa.ccNomeCompleto).");
                setErrorSB(true);
                return;
            }
        } else if (tipoImportacao === "CASOS") {
            if (!mappedValues.includes("tbCaso.ccNomeCaso") || !mappedValues.includes("tbPessoa.ccNomeCompleto")) {
                setErrorMessage("Para importar Casos, mapeie o Nome do Caso (tbCaso.ccNomeCaso) e o Cliente Atrelado (tbPessoa.ccNomeCompleto).");
                setErrorSB(true);
                return;
            }
        } else if (tipoImportacao === "PROCESSOS") {
            if (!mappedValues.includes("tbProcesso.ccProcesso") || !mappedValues.includes("tbCaso.ccNomeCaso") || !mappedValues.includes("tbPessoa.ccNomeCompleto")) {
                setErrorMessage("Para importar Processos, mapeie o Nº do Processo, o Caso e o Cliente.");
                setErrorSB(true);
                return;
            }
        }

        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("mappingJson", JSON.stringify(mapping));
        formData.append("tipoImportacao", tipoImportacao);

        try {
            if (tipoImportacao === "FASE0") {
                // Return CSV file
                const response = await fetch(baseURL + "/process-migration/extract-unique-names", {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Authorization': sessionStorage.getItem('user')
                    }
                });
                if (!response.ok) throw new Error("Erro ao gerar o CSV");
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = "nomes_unicos.csv";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setSuccessSB(true);
                setErrorMessage("CSV de Nomes Únicos gerado com sucesso!");
            } else if (tipoImportacao === "FASE0_5") {
                await postUpload("/process-migration/upload-mapping-csv", formData);
                setSuccessSB(true);
                setErrorMessage("De-Para de Nomes importado com sucesso!");
            } else {
                await postUpload("/process-migration/upload", formData);
                setSuccessSB(true);
                setErrorMessage("Arquivo enviado com sucesso! A migração está sendo processada em segundo plano.");
            }
            
            setFile(null);
            setStep(1);
            setHeaders([]);
            const fileInput = document.getElementById("upload-xml");
            if (fileInput) fileInput.value = "";
        } catch (error) {
            console.error("Erro na migração:", error);
            setErrorMessage(error.response?.data?.message || "Erro ao iniciar a migração.");
            setErrorSB(true);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ 
                    p: 3, 
                    mb: 2, 
                    borderColor: "divider" 
                }}>
                    <MDBox>
                        <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>Migração de Processos (XML)</MDTypography>
                        <MDTypography variant="button" color="text" mb={2} display="block">
                            Módulo de importação em lote de Processos e Casos utilizando De-Para dinâmico de colunas.
                        </MDTypography>
                        
                        <MDBox mt={3} mb={3}>
                            <Divider />
                        </MDBox>

                        {step === 1 && (
                            <MDBox>
                                <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"} mb={2}>
                                    Passo 1: Configurar Importação e Selecionar Arquivo
                                </MDTypography>

                                <MDBox mb={3}>
                                    <FormControl fullWidth>
                                        <InputLabel id="tipo-importacao-label" sx={{ backgroundColor: darkMode ? "#1a2035" : "#fff", px: 1 }}>Tipo de Importação</InputLabel>
                                        <Select
                                            labelId="tipo-importacao-label"
                                            value={tipoImportacao}
                                            onChange={(e) => setTipoImportacao(e.target.value)}
                                            sx={{ minHeight: "44px" }}
                                        >
                                            <MenuItem value="FASE0">Fase 0: Extrair Nomes Únicos para CSV (Gera Planilha)</MenuItem>
                                            <MenuItem value="FASE0_5">Fase 0.5: Importar Planilha CSV de De-Para de Nomes</MenuItem>
                                            <MenuItem value="PESSOAS">Fase 1: Pessoas (Clientes / Autores / Réus)</MenuItem>
                                            <MenuItem value="CASOS">Fase 2: Casos (Pastas / Assuntos)</MenuItem>
                                            <MenuItem value="PROCESSOS">Fase 3: Processos (Ações Judiciais)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </MDBox>

                                <MDBox mt={2} p={3} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px dashed', borderColor: "divider", borderRadius: 2 }} textAlign="center">
                                    <input
                                        accept=".xml"
                                        id="upload-xml"
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="upload-xml">
                                        <MDButton variant="outlined" color="info" component="span" startIcon={<Icon>upload_file</Icon>}>
                                            {tipoImportacao === "FASE0_5" ? "Selecionar Arquivo CSV" : "Selecionar Arquivo XML"}
                                        </MDButton>
                                    </label>
                                    {file && (
                                        <MDBox mt={2}>
                                            <MDTypography variant="body2" color="success">
                                                <Icon sx={{ verticalAlign: 'middle', mr: 1 }}>check_circle</Icon>
                                                {file.name} ({Math.round(file.size / 1024)} KB)
                                            </MDTypography>
                                        </MDBox>
                                    )}
                                </MDBox>

                                <MDBox mt={3} display="flex" justifyContent="flex-end">
                                    {tipoImportacao === "FASE0_5" ? (
                                        <MDButton
                                            variant="gradient"
                                            color="success"
                                            onClick={handleStartMigration}
                                            disabled={!file || uploading}
                                            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <Icon>cloud_upload</Icon>}
                                        >
                                            {uploading ? "Importando..." : "Importar CSV de De-Para"}
                                        </MDButton>
                                    ) : (
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            onClick={handleExtractHeaders}
                                            disabled={!file || loadingHeaders}
                                            startIcon={loadingHeaders ? <CircularProgress size={20} color="inherit" /> : <Icon>east</Icon>}
                                        >
                                            {loadingHeaders ? "Lendo Colunas..." : "Avançar para Mapeamento"}
                                        </MDButton>
                                    )}
                                </MDBox>
                            </MDBox>
                        )}

                                {step === 2 && (
                                    <MDBox>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <MDTypography variant="h6" fontWeight="medium">
                                                Passo 2: Mapear Colunas (De-Para)
                                            </MDTypography>
                                            <MDButton 
                                                variant="text" 
                                                color="info" 
                                                onClick={handleSaveMapping}
                                                disabled={savingMapping}
                                            >
                                                {savingMapping ? <CircularProgress size={15} /> : "Salvar Layout"}
                                            </MDButton>
                                        </MDBox>
                                        
                                        <MDTypography variant="caption" color="text" mb={3} display="block">
                                            As colunas obrigatórias variam conforme o tipo de importação. As colunas não mapeadas serão ignoradas.
                                        </MDTypography>

                                        <Grid container spacing={2}>
                                            {headers.map((colName, idx) => (
                                                <Grid item xs={12} md={6} key={idx}>
                                                    <MDBox display="flex" alignItems="center" gap={2} p={1} sx={{ backgroundColor: darkMode ? "background.default" : "#f8f9fa", border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                        <MDBox flex={1}>
                                                            <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                                                {colName}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <Icon color={darkMode ? "white" : "dark"}>arrow_forward</Icon>
                                                        <MDBox flex={2}>
                                                            <Autocomplete
                                                                options={SYSTEM_FIELDS}
                                                                getOptionLabel={(option) => option.label}
                                                                value={SYSTEM_FIELDS.find(o => o.value === mapping[colName]) || SYSTEM_FIELDS[0]}
                                                                onChange={(e, newVal) => {
                                                                    setMapping(prev => ({
                                                                        ...prev,
                                                                        [colName]: newVal ? newVal.value : ""
                                                                    }));
                                                                }}
                                                                renderInput={(params) => <MDInput {...params} label="Campo do Sistema" />}
                                                                fullWidth
                                                            />
                                                        </MDBox>
                                                    </MDBox>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <MDBox mt={4} display="flex" justifyContent="space-between" alignItems="center">
                                            <MDButton variant="outlined" color="dark" onClick={() => setStep(1)}>
                                                Voltar
                                            </MDButton>
                                            <MDBox display="flex" gap={2}>
                                                <MDButton
                                                    variant="outlined"
                                                    color="info"
                                                    onClick={handleSaveMapping}
                                                    disabled={savingMapping}
                                                    startIcon={savingMapping ? <CircularProgress size={20} color="inherit" /> : <Icon>save</Icon>}
                                                >
                                                    {savingMapping ? "Salvando..." : "Salvar Mapeamento"}
                                                </MDButton>
                                                <MDButton
                                                    variant="gradient"
                                                    color="success"
                                                    onClick={handleStartMigration}
                                                    disabled={uploading}
                                                    startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : (tipoImportacao === 'FASE0' ? <Icon>download</Icon> : <Icon>cloud_upload</Icon>)}
                                                >
                                                    {uploading ? (tipoImportacao === 'FASE0' ? "Gerando..." : "Migrando...") : (tipoImportacao === 'FASE0' ? "Gerar e Baixar CSV" : "Iniciar Migração")}
                                                </MDButton>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>
                                )}
                    </MDBox>
                </Card>
            </Grid>

            <MDSnackbar
                color={errorSB ? "error" : "success"}
                icon={errorSB ? "warning" : "check"}
                title={errorSB ? "Atenção" : "Sucesso"}
                content={errorMessage}
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                autoHideDuration={6000}
                bgWhite={!darkMode}
            />
        </Grid>
    );
}

export default MigracaoProcessos;
